/**
 * Thin fetch wrapper around the auth API.
 *
 * Design choices:
 * - Always sends credentials (cookies are the session transport)
 * - Returns typed success OR throws a structured `ApiError` class
 * - Validation errors from the server bubble up as `ApiError` with `field_errors`
 * - Does NOT do global auth redirects — route guards + React Query handle that
 *
 * All endpoint calls are thin wrappers that give us type safety via the shared
 * Zod schemas. The backend already validates; here we just consume the contract.
 */

import i18n from "./i18n";
import type {
  RegisterRequest,
  RegisterResponse,
  LoginRequest,
  LoginResponse,
  MfaSetupResponse,
  MfaVerifyRequest,
  MfaVerifyResponse,
  MeResponse,
  LogoutResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  SendVerificationEmailResponse,
  VerifyEmailRequest,
  VerifyEmailResponse,
  MfaResetRequestRequest,
  MfaResetRequestResponse,
  MfaResetCancelRequest,
  MfaResetCancelResponse,
  MfaResetConfirmRequest,
  MfaResetConfirmResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
  UpdateProfileRequest,
  UpdateProfileResponse,
  ChangeEmailRequest,
  ChangeEmailResponse,
  VerifyEmailChangeRequest,
  VerifyEmailChangeResponse,
  RegenerateBackupCodesRequest,
  RegenerateBackupCodesResponse,
  TrustedDevicesListResponse,
  TrustedDeviceRevokeResponse,
  TrustedDevicesRevokeAllResponse,
  ApiError as ApiErrorShape,
} from "@sendsms/shared";


/** P2 (01-Sep): what the server shares about MY sessions -- recognisable, never infrastructural. */
export type SessionItem = {
  id: string;
  current: boolean;
  created_at: number;
  last_seen_at: number | null;
  country: string | null;
  device: string;
  mfa_verified: boolean;
};
export type SessionsListResponse = { status: "ok"; sessions: SessionItem[] };

// -----------------------------------------------------------
// Base URL
// -----------------------------------------------------------

/**
 * In dev: Vite proxy maps /api/* → http://127.0.0.1:8787 (see vite.config.ts).
 * In prod: same-origin — Cloudflare Pages serves frontend, routes /api to Worker.
 * Either way, we can use a relative base.
 */
const API_BASE = "/api";

// -----------------------------------------------------------
// Error type
// -----------------------------------------------------------

/**
 * Thrown whenever the API returns a non-2xx response or whenever the response
 * can't be parsed. Holds the status code, the parsed error envelope from the
 * server (if any), and an optional `fieldErrors` dict for form-level display.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly fieldErrors: Record<string, string[]> | undefined;
  readonly retryAfterSeconds: number | undefined;
  readonly requestId: string | null | undefined;
  /** Which rate window blocked (429): the server already tells us. */
  readonly limitWindow: "minute" | "day" | undefined;
  /** The raw envelope - structured errors (V425 numbers) live here. */
  readonly extra: Record<string, unknown>;

  constructor(
    status: number,
    envelope: Partial<ApiErrorShape> & { error?: string; message?: string },
  ) {
    super(envelope.message ?? envelope.error ?? `HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.code = envelope.code ?? envelope.error;
    this.fieldErrors = envelope.field_errors;
    this.retryAfterSeconds = envelope.retry_after_seconds;
    this.requestId = envelope.request_id;
    const raw = envelope as Record<string, unknown>;
    this.limitWindow =
      raw.limit_window === "day" || raw.limit_window === "minute"
        ? raw.limit_window
        : undefined;
    this.extra = raw;
  }

  /** True for 401/403 — the session is invalid or insufficient. */
  get isAuthError(): boolean {
    return this.status === 401 || this.status === 403;
  }

  /** True for 429 — rate limited. */
  get isRateLimited(): boolean {
    return this.status === 429;
  }

  /** True when the server returned per-field validation errors. */
  get hasFieldErrors(): boolean {
    return (
      this.fieldErrors !== undefined &&
      Object.keys(this.fieldErrors).length > 0
    );
  }
}

// -----------------------------------------------------------
// Core request
// -----------------------------------------------------------

type RequestOpts = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
};

export type PreferencesResponse = {
  phone_default_country: string | null;
  self_msg_per_minute: number | null;
  /** SEC-04 f2: the user's own monthly budget in cents; 0 = off. */
  monthly_budget_cents: number;
  // Sending speed (msg/s), §9. self = user's own cap (null = defer to ceiling).
  self_throttle_msg_per_sec: number | null;
  notification_locale: "ro" | "en";
  admin_limits: {
    max_msg_per_minute: number;
    max_msg_per_day: number;
    // Hard speed ceiling — admin/owner only. Read-only for the user.
    throttle_msg_per_sec: number;
  };
  // Inbound (MO) webhook — read-only URL + token to paste into sendSMS console.
  mo_webhook: {
    url: string;
    token: string;
    instructions: string;
  };
  [key: string]: unknown;
};
export type UpdatePreferencesRequest = {
  phone_default_country?: string | null;
  self_msg_per_minute?: number | null;
  monthly_budget_cents?: number | null;
  self_throttle_msg_per_sec?: number | null;
  notification_locale?: "ro" | "en";
};

// SUB-USERS SU3-lite (19-Oct-2026): shapes of /api/team (routes/team.ts). Never a code hash, never the code itself.
export type TeamStatus = { enabled: boolean; max_sub_users: number; requested_at: number | null; members: number; invites: number };
export type TeamMember = { id: string; email: string; full_name: string | null; status: string; suspension_reason: string | null; balance_mode: string; balance_micro_eur?: number; created_at: number; last_login_at: number | null };
// SU2b-1 (23-Oct-2026): owner <-> sub-account money (GET /api/team/:id/wallet, POST /transfer | /return). The TOTP code travels once, in the body.
export type TeamMemberTransfer = { id: string; kind: "balance" | "return"; direction: "to_member" | "to_owner"; amount_micro_eur: number; created_at: number };
// SU2b-1b-WEB (28-Oct-2026): the 4 shared-balance fields are optional on purpose (older Worker = undefined = own balance).
export type TeamMemberWallet = { member_id: string; balance_micro_eur: number; account_type: string; credit_limit_micro_eur: number; transfers: TeamMemberTransfer[]; balance_mode?: string; monthly_budget_cents?: number | null; month_consumed_micro_eur?: number; month_resets_at?: number };
export type TeamBalanceModeResult = { member_id: string; balance_mode: "individual" | "shared"; returned_micro_eur: number };
export type TeamMonthlyCapResult = { member_id: string; monthly_budget_cents: number | null };
export type TeamMoneyResult = { transfer_id: string; kind: "balance" | "return"; amount_micro_eur: number; owner_balance_after_micro_eur: number; member_balance_after_micro_eur: number };
export type TeamInvite = { id: string; code_prefix: string; email: string; full_name: string | null; status: string; created_at: number; expires_at: number; consumed_at: number | null };
export type TeamList = { max_sub_users: number; slots_left: number; members: TeamMember[]; invites: TeamInvite[] };
export type TeamInviteIssued = { id: string; code_prefix: string; expires_at: number };
export type TeamMemberActionResult = { id: string; status: "active" | "suspended"; suspension_reason: string | null; keys_revoked: number; sessions_revoked: number };

async function request<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const { method = "GET", body, signal } = opts;

  const headers: Record<string, string> = {
    Accept: "application/json",
    // Tell the API which language to use for server-rendered content like
    // transactional emails. Server validates against a strict allowlist.
    "Accept-Language": i18n.language || "ro",
  };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      credentials: "include",
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (err) {
    // Network failure — DNS, offline, request aborted, CORS preflight failed
    if (err instanceof DOMException && err.name === "AbortError") {
      throw err;
    }
    throw new ApiError(0, {
      error: "network_error",
      message:
        err instanceof Error ? err.message : "Network request failed.",
    });
  }

  // 204 No Content — return empty object cast to T (rare, but possible)
  if (response.status === 204) {
    return {} as T;
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    // Server returned non-JSON body — treat as opaque error
    throw new ApiError(response.status, {
      error: "invalid_response",
      message: `Server returned non-JSON response (status ${response.status}).`,
    });
  }

  if (!response.ok) {
    const envelope = (payload ?? {}) as Partial<ApiErrorShape>;
    throw new ApiError(response.status, envelope);
  }

  return payload as T;
}

// -----------------------------------------------------------
// Endpoint functions
// -----------------------------------------------------------

/** MFA-RESET-UX-1: what a reset link is right now (the API answers only for a valid link). */
export type MfaResetVerifyResponse = {
  status: "ok";
  in_delay_window: boolean;
  not_before: number;
  expires_at: number;
  retry_after_seconds: number;
};

export const api = {
  // ---------- Registration & session ----------

  register(body: RegisterRequest): Promise<RegisterResponse> {
    return request<RegisterResponse>("/auth/register", {
      method: "POST",
      body,
    });
  },

  /**
   * Sprint INVITE.6: check a registration code before showing the
   * form. Returns { valid, trusted_email? }. Generic negative on any
   * failure (no enumeration).
   */
  verifyInvite(body: {
    code: string;
  }): Promise<{ valid: boolean; trusted_email?: string }> {
    return request<{ valid: boolean; trusted_email?: string }>(
      "/auth/register/verify-invite",
      { method: "POST", body },
    );
  },

  /** SUB-USERS SU2a: check a team invitation code before showing the accept form (generic negative, V610). */
  teamInviteVerify(body: { code: string }): Promise<{ valid: boolean; email?: string; full_name?: string | null; organization_name?: string; owner_name?: string }> {
    return request<{ valid: boolean; email?: string; full_name?: string | null; organization_name?: string; owner_name?: string }>(
      "/auth/team-invite/verify",
      { method: "POST", body },
    );
  },

  /** SUB-USERS SU2a: create the member account; no session -- the member signs in and sets up MFA. */
  teamInviteAccept(body: { code: string; email: string; password: string; full_name: string; phone: string; tz?: string }): Promise<{ status: "ok"; requires_mfa_setup: boolean }> {
    return request<{ status: "ok"; requires_mfa_setup: boolean }>("/auth/team-invite/accept", { method: "POST", body });
  },

  // SUB-USERS SU3-lite (19-Oct-2026): the owner Team tab. Every route re-checks owner role + MFA + the organization gate server-side.
  teamStatus(): Promise<TeamStatus> {
    return request<TeamStatus>("/team/status");
  },
  teamRequestActivation(body: { note?: string }): Promise<{ requested_at: number }> {
    return request<{ requested_at: number }>("/team/request-activation", { method: "POST", body });
  },
  teamList(): Promise<TeamList> {
    return request<TeamList>("/team");
  },
  teamInvite(body: { email: string; full_name?: string }): Promise<TeamInviteIssued> {
    return request<TeamInviteIssued>("/team/invites", { method: "POST", body });
  },
  teamInviteRevoke(id: string): Promise<{ id: string; status: "revoked" }> {
    return request<{ id: string; status: "revoked" }>(`/team/invites/${encodeURIComponent(id)}/revoke`, { method: "POST" });
  },
  teamInviteResend(id: string): Promise<TeamInviteIssued> {
    return request<TeamInviteIssued>(`/team/invites/${encodeURIComponent(id)}/resend`, { method: "POST" });
  },
  teamMemberAction(id: string, action: "suspend" | "reactivate" | "remove"): Promise<TeamMemberActionResult> {
    return request<TeamMemberActionResult>(`/team/${encodeURIComponent(id)}/${action}`, { method: "POST" });
  },
  teamMemberWallet(id: string): Promise<TeamMemberWallet> {
    return request<TeamMemberWallet>(`/team/${encodeURIComponent(id)}/wallet`);
  },
  teamMoney(id: string, kind: "transfer" | "return", body: { amount_micro_eur: number; note?: string; totp_code: string }): Promise<TeamMoneyResult> {
    return request<TeamMoneyResult>(`/team/${encodeURIComponent(id)}/${kind}`, { method: "POST", body });
  },
  // SU2b-1b-WEB: balance mode (TOTP step-up, server returns the member's balance first) and the monthly cap (no money moves).
  teamBalanceMode(id: string, body: { mode: "individual" | "shared"; totp_code: string }): Promise<TeamBalanceModeResult> {
    return request<TeamBalanceModeResult>(`/team/${encodeURIComponent(id)}/balance-mode`, { method: "POST", body });
  },
  teamMonthlyCap(id: string, body: { monthly_budget_cents: number | null }): Promise<TeamMonthlyCapResult> {
    return request<TeamMonthlyCapResult>(`/team/${encodeURIComponent(id)}/monthly-cap`, { method: "POST", body });
  },

  login(body: LoginRequest): Promise<LoginResponse> {
    return request<LoginResponse>("/auth/login", { method: "POST", body });
  },

  logout(): Promise<LogoutResponse> {
    return request<LogoutResponse>("/auth/logout", { method: "POST" });
  },

  // LAU F2b: end the current impersonation session. Server auto-authorizes
  // from the signed cookie (no client-supplied id); returns { status }.
  impersonationExit(): Promise<{ status: string }> {
    return request<{ status: string }>("/auth/impersonation/exit", {
      method: "POST",
    });
  },

  me(opts?: { signal?: AbortSignal }): Promise<MeResponse> {
    return request<MeResponse>("/auth/me", { signal: opts?.signal });
  },

  // ---------- MFA ----------

  mfaSetup(): Promise<MfaSetupResponse> {
    return request<MfaSetupResponse>("/auth/mfa/setup", { method: "POST" });
  },

  mfaVerify(body: MfaVerifyRequest): Promise<MfaVerifyResponse> {
    return request<MfaVerifyResponse>("/auth/mfa/verify", {
      method: "POST",
      body,
    });
  },

  /** MFA-REENROL-1: change the authenticator on the spot. start = current code -> new QR; confirm = new code. */
  mfaReenrollStart(body: RegenerateBackupCodesRequest): Promise<MfaSetupResponse> {
    return request<MfaSetupResponse>("/auth/mfa/reenroll/start", { method: "POST", body });
  },
  mfaReenrollConfirm(body: RegenerateBackupCodesRequest): Promise<{ status: "ok"; trusted_devices_revoked: number }> {
    return request<{ status: "ok"; trusted_devices_revoked: number }>("/auth/mfa/reenroll/confirm", { method: "POST", body });
  },

  mfaRegenerateBackupCodes(
    body: RegenerateBackupCodesRequest,
  ): Promise<RegenerateBackupCodesResponse> {
    return request<RegenerateBackupCodesResponse>(
      "/auth/mfa/regenerate-backup-codes",
      { method: "POST", body },
    );
  },

  mfaResetRequest(
    body: MfaResetRequestRequest,
  ): Promise<MfaResetRequestResponse> {
    return request<MfaResetRequestResponse>("/auth/mfa/reset-request", {
      method: "POST",
      body,
    });
  },

  mfaResetCancel(
    body: MfaResetCancelRequest,
  ): Promise<MfaResetCancelResponse> {
    return request<MfaResetCancelResponse>("/auth/mfa/reset-cancel", {
      method: "POST",
      body,
    });
  },

  mfaResetConfirm(
    body: MfaResetConfirmRequest,
  ): Promise<MfaResetConfirmResponse> {
    return request<MfaResetConfirmResponse>("/auth/mfa/reset-confirm", {
      method: "POST",
      body,
    });
  },

  /** MFA-RESET-UX-1: read-only pre-check of a reset link (valid / inside the 24 h window / when it opens). */
  mfaResetVerify(body: MfaResetCancelRequest): Promise<MfaResetVerifyResponse> {
    return request<MfaResetVerifyResponse>("/auth/mfa/reset-verify", {
      method: "POST",
      body,
    });
  },

  // ---------- Password ----------

  forgotPassword(
    body: ForgotPasswordRequest,
  ): Promise<ForgotPasswordResponse> {
    return request<ForgotPasswordResponse>("/auth/forgot-password", {
      method: "POST",
      body,
    });
  },

  resetPassword(body: ResetPasswordRequest): Promise<ResetPasswordResponse> {
    return request<ResetPasswordResponse>("/auth/reset-password", {
      method: "POST",
      body,
    });
  },

  changePassword(
    body: ChangePasswordRequest,
  ): Promise<ChangePasswordResponse> {
    return request<ChangePasswordResponse>("/auth/change-password", {
      method: "POST",
      body,
    });
  },

  // V389/V391: server enforces strict whitelist + Unicode validation.
  updateProfile(body: UpdateProfileRequest): Promise<UpdateProfileResponse> {
    return request<UpdateProfileResponse>("/auth/profile", {
      method: "PATCH",
      body,
    });
  },

  // ---------- User preferences (notifications, phone country, self limits) ----
  getPreferences(): Promise<PreferencesResponse> {
    return request<PreferencesResponse>("/me/preferences");
  },
  updatePreferences(
    body: UpdatePreferencesRequest,
  ): Promise<PreferencesResponse> {
    return request<PreferencesResponse>("/me/preferences", {
      method: "PATCH",
      body,
    });
  },

  // ---------- Email verification ----------

  sendVerificationEmail(): Promise<SendVerificationEmailResponse> {
    return request<SendVerificationEmailResponse>(
      "/auth/send-verification-email",
      { method: "POST" },
    );
  },

  verifyEmail(body: VerifyEmailRequest): Promise<VerifyEmailResponse> {
    return request<VerifyEmailResponse>("/auth/verify-email", {
      method: "POST",
      body,
    });
  },

  // ---------- Change email (verified, pending pattern) ----------

  changeEmail(body: ChangeEmailRequest): Promise<ChangeEmailResponse> {
    return request<ChangeEmailResponse>("/auth/change-email", {
      method: "POST",
      body,
    });
  },

  verifyEmailChange(
    body: VerifyEmailChangeRequest,
  ): Promise<VerifyEmailChangeResponse> {
    return request<VerifyEmailChangeResponse>("/auth/verify-email-change", {
      method: "POST",
      body,
    });
  },

  // ---------- Trusted devices ----------

  trustedDevicesList(): Promise<TrustedDevicesListResponse> {
    return request<TrustedDevicesListResponse>("/auth/trusted-devices");
  },

  trustedDevicesRevoke(id: string): Promise<TrustedDeviceRevokeResponse> {
    return request<TrustedDeviceRevokeResponse>(
      `/auth/trusted-devices/${encodeURIComponent(id)}`,
      { method: "DELETE" },
    );
  },

  trustedDevicesRevokeAll(): Promise<TrustedDevicesRevokeAllResponse> {
    return request<TrustedDevicesRevokeAllResponse>("/auth/trusted-devices", {
      method: "DELETE",
    });
  },

  // ---------- My sessions (P2, 01-Sep) ----------

  sessionsList(): Promise<SessionsListResponse> {
    return request<SessionsListResponse>("/auth/sessions");
  },

  sessionsRevoke(id: string): Promise<{ status: "ok" }> {
    return request<{ status: "ok" }>(`/auth/sessions/${encodeURIComponent(id)}`, { method: "DELETE" });
  },

  sessionsRevokeOthers(): Promise<{ status: "ok"; revoked_count: number }> {
    return request<{ status: "ok"; revoked_count: number }>("/auth/sessions/revoke-others", { method: "POST" });
  },
};
